import { Agenda, AgendaItemDoc, Event, Track } from '@livekatsomo/models';
import { ListItemLinkButton } from '@livekatsomo/web/ui-components/link';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useAgendaItemsWithStartTime } from '../useAgendaItemsWithStartTime/useAgendaItemsWithStartTime';
import { AgendaListItem } from './AgendaListItem';

/**
 * Props for the AgendaTable component.
 */
export interface AgendaTableProps {
  /**
   * The event associated with the agenda.
   */
  event: Event;
  /**
   * The track associated with the agenda.
   */
  track: Track;
  /**
   * The list of agenda items to display.
   */
  agendaItems: Agenda;
  /**
   * The currently selected agenda item. Synced to video player timestamp.
   */
  currentAgendaItem?: AgendaItemDoc;
}

/**
 * Renders a list of agenda items for a given event and track.
 * @returns The rendered list of agenda items.
 */
export function AgendaList({
  agendaItems,
  event,
  track,
  currentAgendaItem,
}: AgendaTableProps) {
  const { t } = useTranslation();
  const agendaItemsWithDates = useAgendaItemsWithStartTime(
    agendaItems,
    event.startDate,
    track.startTimeDelta,
    track.mode,
    currentAgendaItem?.id,
  );
  const router = useRouter();

  return (
    <List sx={{ p: 0 }} aria-label={t('Agenda')}>
      {agendaItemsWithDates.map((agendaItem, index) => {
        if (track.mode === 'on-demand' && agendaItem.timestamp) {
          return (
            <AgendaListItem
              component={ListItemLinkButton}
              key={agendaItem.id}
              agendaItem={agendaItem}
              to={{
                pathname: router.pathname,
                query: {
                  ...router.query,
                  timestamp: agendaItem.timestamp?.getTime().toString(),
                },
              }}
              prefetch={false}
              shallow={true}
              replace={true}
            />
          );
        } else {
          return (
            <AgendaListItem
              key={agendaItem.id}
              component={ListItemButton}
              agendaItem={agendaItem}
              selected={
                track.mode === 'live' &&
                currentAgendaItem &&
                currentAgendaItem.id === agendaItem.id
              }
            />
          );
        }
      })}
    </List>
  );
}
